// Care packages — curated one-click boxes, each anchored to ONE merchant.
// Two public marketing pages:
//
//   /packages          gallery of all active boxes
//   /packages/:slug    full item list (real prices) + "Send this package"
//
// Every box belongs to a specific merchant in a specific city, and every
// line is a real in-stock item, so the total is exact. The send flow
// requires an authenticated customer and only asks for a recipient — the
// merchant is fixed by the box. Non-customers are pushed to register.

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ShoppingBag, ArrowRight, ArrowLeft, Loader2, Store, MapPin, User as UserIcon, Package,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  carePackages, recipients,
  type CarePackageSummary, type CarePackageDetail,
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { iconFor } from '../lib/icons';
import { formatHtgMinor, formatDisplayFromHtgMinor, deliveryEtaLabel } from '../lib/money';
import { Button } from '../components/stripe-ui/Button';
import { Modal } from '../components/shared/Modal';

// ─── Marketing chrome (lightweight; Landing's header isn't exported) ──

function Chrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white text-[var(--color-ink)] flex flex-col">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[var(--color-hairline)]">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white">
              <ShoppingBag className="w-4 h-4" />
            </span>
            <span className="font-semibold tracking-tight text-[17px]">VoyeLakay</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline text-[13px] text-[var(--color-body)] hover:text-[var(--color-ink)] px-3 py-2">Sign in</Link>
            <Link
              to="/register?role=customer"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[var(--color-ink)] text-white text-[13px] font-semibold hover:bg-[var(--color-primary)] transition-colors"
            >
              Get started
            </Link>
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-[var(--color-hairline)] py-8 mt-12">
        <div className="mx-auto max-w-7xl px-6 text-[12px] text-[var(--color-muted)]">
          Each box is fulfilled by the named merchant at their listed prices. USD shown at ≈ 132.4 HTG / USD; the rate locks at quote time.
        </div>
      </footer>
    </div>
  );
}

function EtaBadge({ hours }: { hours: number | null | undefined }) {
  const eta = deliveryEtaLabel(hours);
  if (!eta) return null;
  const tone: Record<string, string> = {
    emerald: 'bg-[var(--color-success-bg)] text-[var(--color-success-fg)]',
    sky:     'bg-[#EAF0FE] text-[#063B9E]',
    amber:   'bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]',
    slate:   'bg-[var(--color-panel)] text-[var(--color-secondary)]',
  };
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${tone[eta.tone]}`}>{eta.label}</span>;
}

// ─── Gallery page ────────────────────────────────────────────

export function CarePackagesPage() {
  const q = useQuery({
    queryKey: ['storefront', 'packages'],
    queryFn:  carePackages.list,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Chrome>
      <section className="border-b border-[var(--color-hairline)] bg-gradient-to-br from-brand-50 via-white to-[var(--color-warning-bg)]/40">
        <div className="mx-auto max-w-7xl px-6 py-14 md:py-20">
          <p className="text-[11px] uppercase tracking-[0.2em] text-brand-600 font-semibold inline-flex items-center gap-1.5">
            <Package className="w-3 h-3" /> One-click care packages
          </p>
          <h1 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-[var(--color-ink)] max-w-3xl leading-[1.08]">
            Send a ready-made box home in seconds
          </h1>
          <p className="mt-4 text-[15px] md:text-[17px] text-[var(--color-secondary)] max-w-2xl leading-relaxed">
            Hand-picked boxes from real merchants across Haiti — a full week of groceries from Léogâne, a holiday spread from Cap-Haïtien,
            a pharmacy refill from Port-au-Prince. Pick one, choose who it's for, and the merchant fills it.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12">
        {q.isLoading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-56 rounded-2xl border border-[var(--color-hairline)] bg-white animate-pulse" />
            ))}
          </div>
        )}

        {q.error && (
          <div className="text-center py-16 text-[13px] text-[var(--color-secondary-2)] inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="w-4 h-4" /> Couldn't load care packages right now.
          </div>
        )}

        {q.data && q.data.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {q.data.map((p) => <PackageCard key={p.id} pkg={p} />)}
          </div>
        )}

        {q.data && q.data.length === 0 && (
          <div className="text-center py-16 text-[13px] text-[var(--color-secondary-2)]">No packages available yet.</div>
        )}
      </section>
    </Chrome>
  );
}

function PackageCard({ pkg }: { pkg: CarePackageSummary }) {
  const Icon = iconFor(pkg.system_icon);
  return (
    <Link
      to={`/packages/${pkg.slug}`}
      className="group bg-white border border-[var(--color-hairline)] rounded-2xl p-5 hover:border-[var(--color-border)] hover:shadow-md transition-all flex flex-col"
    >
      <div className="flex items-start justify-between">
        <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-50 to-[var(--color-warning-bg)]/50 text-brand-600 mb-4">
          <Icon className="w-6 h-6" />
        </div>
        <EtaBadge hours={pkg.merchant?.avg_fulfillment_hours} />
      </div>
      <h3 className="text-[16px] font-semibold tracking-tight text-[var(--color-ink)]">{pkg.name}</h3>
      {pkg.subtitle && <p className="mt-1 text-[12.5px] text-[var(--color-secondary-2)] leading-relaxed">{pkg.subtitle}</p>}

      {pkg.merchant && (
        <p className="mt-2 text-[11.5px] text-[var(--color-secondary-2)] inline-flex items-center gap-1">
          <Store className="w-3 h-3 text-[var(--color-muted)]" /> {pkg.merchant.business_name}
          {pkg.merchant.city && <><span className="text-[var(--color-muted)]">·</span><MapPin className="w-3 h-3 text-[var(--color-muted)]" /> {pkg.merchant.city.name}</>}
        </p>
      )}

      {pkg.items_preview.length > 0 && (
        <p className="mt-3 text-[11.5px] text-[var(--color-muted)] leading-relaxed line-clamp-2">
          {pkg.items_preview.join(' · ')}{pkg.items_count > pkg.items_preview.length ? ' …' : ''}
        </p>
      )}

      <div className="mt-auto pt-4 flex items-baseline justify-between">
        <div>
          {pkg.approx_htg_minor != null ? (
            <>
              <span className="text-[15px] font-semibold text-[var(--color-ink)] font-mono">≈ {formatDisplayFromHtgMinor(pkg.approx_htg_minor)}</span>
              <span className="text-[11px] text-[var(--color-muted)] ml-1.5">{pkg.items_count} items</span>
            </>
          ) : (
            <span className="text-[12.5px] text-[var(--color-secondary-2)]">{pkg.items_count} items</span>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 group-hover:gap-2 transition-all">
          View <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}

// ─── Detail page ─────────────────────────────────────────────

export function PackageDetailPage() {
  const { slug = '' } = useParams();
  const { user } = useAuth();
  const [sendOpen, setSendOpen] = useState(false);
  const [sp] = useSearchParams();

  const q = useQuery({
    queryKey: ['storefront', 'packages', slug],
    queryFn:  () => carePackages.get(slug),
    enabled:  !!slug,
  });

  const pkg = q.data;
  const Icon = iconFor(pkg?.system_icon);
  const isCustomer = user?.role === 'customer';

  // Returning from sign-in with ?send=1 (set by the "Sign in to send" link)
  // re-opens the send modal automatically, so the picked box isn't lost.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!autoOpened.current && sp.get('send') === '1' && isCustomer && pkg) {
      autoOpened.current = true;
      setSendOpen(true);
    }
  }, [sp, isCustomer, pkg]);

  return (
    <Chrome>
      <section className="mx-auto max-w-4xl px-6 py-10">
        <Link to="/packages" className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--color-secondary-2)] hover:text-[var(--color-ink)]">
          <ArrowLeft className="w-3.5 h-3.5" /> All packages
        </Link>

        {q.isLoading && <div className="mt-8 h-64 rounded-2xl border border-[var(--color-hairline)] bg-white animate-pulse" />}

        {q.error && (
          <div className="mt-8 text-center py-16 text-[13px] text-[var(--color-secondary-2)]">
            We couldn't find that package. <Link to="/packages" className="text-brand-600 hover:underline">Browse all packages</Link>.
          </div>
        )}

        {pkg && (
          <>
            <div className="mt-6 flex items-start gap-4">
              <div className="inline-flex w-14 h-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-50 to-[var(--color-warning-bg)]/50 text-brand-600">
                <Icon className="w-7 h-7" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[var(--color-ink)]">{pkg.name}</h1>
                {pkg.subtitle && <p className="mt-1 text-[14px] text-[var(--color-secondary-2)]">{pkg.subtitle}</p>}
                {pkg.merchant && (
                  <p className="mt-2 text-[12.5px] text-[var(--color-secondary)] inline-flex items-center gap-1.5 flex-wrap">
                    <Store className="w-3.5 h-3.5 text-[var(--color-muted)]" /> {pkg.merchant.business_name}
                    {pkg.merchant.city && <><span className="text-[var(--color-muted)]">·</span><MapPin className="w-3.5 h-3.5 text-[var(--color-muted)]" /> {pkg.merchant.city.name}</>}
                    <EtaBadge hours={pkg.merchant.avg_fulfillment_hours} />
                  </p>
                )}
              </div>
            </div>

            {pkg.description && <p className="mt-5 text-[14px] text-[var(--color-secondary)] leading-relaxed max-w-2xl">{pkg.description}</p>}

            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {/* Item list */}
              <div className="md:col-span-2">
                <h2 className="text-[12px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)] mb-3">
                  What's inside · {pkg.items.length} items
                </h2>
                <ul className="bg-white border border-[var(--color-border)] rounded-xl divide-y divide-[var(--color-hairline)]">
                  {pkg.items.map((it) => {
                    const mi = it.merchant_item;
                    const qty = Number(it.default_quantity);
                    const ItemIcon = iconFor(mi?.system_icon);
                    const line = mi ? mi.price_htg_minor * qty : null;
                    return (
                      <li key={it.id} className="px-4 py-3 flex items-center gap-3">
                        <span className="inline-flex w-8 h-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-50 to-[var(--color-warning-bg)]/40 text-brand-600 shrink-0">
                          <ItemIcon className="w-4 h-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13.5px] font-medium text-[var(--color-ink)] truncate">{it.item_name}</p>
                          {mi && <p className="text-[10.5px] text-[var(--color-secondary-2)]">{mi.unit} · {formatHtgMinor(mi.price_htg_minor)}{qty !== 1 ? ` × ${qty}` : ''}</p>}
                        </div>
                        <span className="text-[12px] font-mono text-[var(--color-secondary-2)] shrink-0 w-7 text-right">×{qty}</span>
                        {line != null && <span className="text-[12.5px] font-mono font-semibold text-[var(--color-ink)] shrink-0 w-20 text-right">{formatHtgMinor(line)}</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Send card */}
              <div className="md:sticky md:top-20 self-start">
                <div className="bg-white border border-[var(--color-border)] rounded-xl p-5">
                  {pkg.approx_htg_minor != null ? (
                    <>
                      <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">Box total</p>
                      <p className="mt-1 text-2xl font-semibold tracking-tight text-[var(--color-ink)] font-mono">
                        ≈ {formatDisplayFromHtgMinor(pkg.approx_htg_minor)}
                      </p>
                      <p className="text-[11.5px] text-[var(--color-muted)] font-mono">{formatHtgMinor(pkg.approx_htg_minor)}</p>
                    </>
                  ) : (
                    <p className="text-[13px] text-[var(--color-secondary)]">{pkg.items.length} curated items.</p>
                  )}

                  <p className="mt-3 text-[11.5px] text-[var(--color-secondary-2)] leading-relaxed">
                    {pkg.merchant ? <>Fulfilled by <strong className="text-[var(--color-body)]">{pkg.merchant.business_name}</strong>. </> : null}
                    The merchant confirms availability and adds delivery in the final quote — you approve the USD total before paying.
                  </p>

                  {isCustomer ? (
                    <Button fullWidth size="lg" className="mt-4" onClick={() => setSendOpen(true)} trailingIcon={<ArrowRight className="w-3.5 h-3.5" />}>
                      Send this package
                    </Button>
                  ) : (
                    <Link
                      to={`/login?returnTo=${encodeURIComponent(`/packages/${pkg.slug}?send=1`)}`}
                      className="mt-4 inline-flex w-full items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-brand-600 text-white text-[13px] font-semibold hover:bg-brand-700"
                    >
                      Sign in to send <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                  {!isCustomer && (
                    <p className="mt-2 text-[11px] text-[var(--color-muted)] text-center">We'll bring you right back to this box after you sign in.</p>
                  )}
                </div>
              </div>
            </div>

            {isCustomer && <SendPackageModal open={sendOpen} onClose={() => setSendOpen(false)} pkg={pkg} />}
          </>
        )}
      </section>
    </Chrome>
  );
}

// ─── Send modal (authenticated customer) ─────────────────────

function SendPackageModal({ open, onClose, pkg }: { open: boolean; onClose: () => void; pkg: CarePackageDetail }) {
  const navigate = useNavigate();
  const recipQ = useQuery({ queryKey: ['me', 'recipients'], queryFn: recipients.list, enabled: open });

  const [recipientId, setRecipientId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (recipientId === '' && recipQ.data && recipQ.data.length > 0) setRecipientId(recipQ.data[0]!.id);
  }, [recipQ.data, recipientId]);

  const send = useMutation({
    mutationFn: () => {
      if (!recipientId) throw new Error('Pick a recipient.');
      return carePackages.createRequest({
        package_slug: pkg.slug,
        recipient_id: Number(recipientId),
        notes:        notes.trim() || null,
      }, { submit: true });
    },
    onSuccess: (req) => {
      toast.success(`Package sent — ${req.cart_item_count} items, ${formatHtgMinor(req.cart_subtotal_htg_minor)}`);
      onClose();
      navigate(`/customer/requests/${req.id}`);
    },
    onError: (e: Error) => toast.error(e.message || 'Could not send the package.'),
  });

  const noRecipients = recipQ.data && recipQ.data.length === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Send "${pkg.name}"`}
      subtitle={pkg.merchant ? `Fulfilled by ${pkg.merchant.business_name}${pkg.merchant.city ? ` · ${pkg.merchant.city.name}` : ''}` : 'Choose who it’s for.'}
      footer={
        <>
          <Button tone="secondary" onClick={onClose} disabled={send.isPending}>Cancel</Button>
          <Button onClick={() => send.mutate()} loading={send.isPending} disabled={!recipientId || send.isPending}>
            Send &amp; submit
          </Button>
        </>
      }
    >
      {noRecipients ? (
        <div className="text-center py-6">
          <p className="text-[13.5px] text-[var(--color-body)] font-medium">You have no recipients yet.</p>
          <p className="mt-1 text-[12.5px] text-[var(--color-secondary-2)]">Add a family member first, then send them a package.</p>
          <Link to="/customer/recipients" className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-600 hover:underline">
            Add a recipient <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)] inline-flex items-center gap-1">
              <UserIcon className="w-3 h-3" /> For
            </span>
            <select
              value={recipientId === '' ? '' : String(recipientId)}
              onChange={(e) => setRecipientId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={recipQ.isLoading || (recipQ.data ?? []).length === 0}
              className="mt-1 w-full px-3 py-2 text-[13px] bg-white border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 disabled:bg-[var(--color-panel)] disabled:text-[var(--color-muted)]"
            >
              {(recipQ.data ?? []).length === 0
                ? <option value="">No recipients</option>
                : (recipQ.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.full_name}{r.city?.name ? ` · ${r.city.name}` : ''}</option>)}
            </select>
            {pkg.merchant?.city && (recipQ.data ?? []).length > 0 && (
              <p className="mt-1 text-[10.5px] text-[var(--color-muted)]">This box ships from {pkg.merchant.city.name}. Delivery to other cities may add time/cost in the quote.</p>
            )}
          </label>

          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">Note for the merchant (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full px-2.5 py-2 text-[13px] bg-white border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400"
              placeholder="Brand preferences, delivery timing, etc."
            />
          </label>

          <p className="text-[11.5px] text-[var(--color-muted)] leading-relaxed">
            All {pkg.items.length} items come from {pkg.merchant?.business_name ?? 'this merchant'}'s in-stock catalog at their listed prices.
          </p>
        </div>
      )}
    </Modal>
  );
}
